// src/hooks/useSchedule.ts
import { useState, useEffect } from "react";
import { fetchClinicCompany } from "../../api/admin/companyApi";
import {
	TeamMember,
	TimeBlock,
	AvailabilitySchedule,
	migrateLegacySchedule
} from "../../types/admin/TeamTypes";
import { isWithinWallClockBlock, toWallClock, wallClockMinutes, wallClockSpanMinutes } from "../../utils/schedule/wallClock";

// Wall clock, "HH:mm" — see utils/schedule/wallClock.
export interface DaySchedule {
	start: string | null;
	end: string | null;
}

export interface LegacySchedule {
	[day: string]: DaySchedule;
}

export const defaultSchedule: LegacySchedule = {
	Monday: { start: "09:00", end: "17:00" },
	Tuesday: { start: "09:00", end: "17:00" },
	Wednesday: { start: "09:00", end: "17:00" },
	Thursday: { start: "09:00", end: "17:00" },
	Friday: { start: "09:00", end: "17:00" },
	Saturday: { start: null, end: null },
	Sunday: { start: null, end: null },
};

/**
 * Generate a UUID v4 compatible string
 */
function generateUUID(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}

	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		const r = Math.random() * 16 | 0;
		const v = c === 'x' ? r : ((r & 0x3) | 0x8);
		return v.toString(16);
	});
}

function minutesToWallClock(minutes: number): string {
	return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function convertLegacyToMultiBlock(legacySchedule: LegacySchedule): AvailabilitySchedule {
	const multiBlockSchedule: AvailabilitySchedule = {};

	for (const [day, schedule] of Object.entries(legacySchedule)) {
		if (schedule.start && schedule.end) {
			multiBlockSchedule[day] = [{
				id: generateUUID(),
				start: schedule.start,
				end: schedule.end
			}];
		} else {
			multiBlockSchedule[day] = [];
		}
	}

	return multiBlockSchedule;
}

function parseScheduleData(data: any): AvailabilitySchedule {
	let scheduleObj = data;

	// If data is a string, try to parse it as JSON
	if (typeof data === 'string') {
		try {
			scheduleObj = JSON.parse(data);
		} catch (err) {
			console.error("Error parsing schedule JSON string:", err);
			return {};
		}
	}

	if (!scheduleObj || typeof scheduleObj !== 'object') {
		console.warn("Invalid schedule data:", scheduleObj);
		return {};
	}

	// Check if this is legacy format (has start/end properties instead of arrays)
	const needsMigration = Object.values(scheduleObj).some(
		(dayData: any) => dayData &&
			!Array.isArray(dayData) &&
			typeof dayData === 'object' &&
			('start' in dayData || 'end' in dayData)
	);

	if (needsMigration) {
		console.log("Migrating legacy schedule format to multi-block");
		return migrateLegacySchedule(scheduleObj);
	}

	const result: AvailabilitySchedule = {};
	const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

	for (const day of days) {
		const dayData = scheduleObj[day];

		if (Array.isArray(dayData)) {
			// Already in multi-block format
			result[day] = dayData.map((block: any) => ({
				id: block.id || generateUUID(),
				start: toWallClock(block.start),
				end: toWallClock(block.end)
			}));
		} else if (dayData && typeof dayData === 'object' && ('start' in dayData || 'end' in dayData)) {
			// Single block legacy format
			if (dayData.start || dayData.end) {
				result[day] = [{
					id: generateUUID(),
					start: toWallClock(dayData.start),
					end: toWallClock(dayData.end)
				}];
			} else {
				result[day] = [];
			}
		} else {
			result[day] = [];
		}
	}

	return result;
}

export function useSchedule(
	selectedTeamMember: TeamMember | null
): AvailabilitySchedule {
	const [schedule, setSchedule] = useState<AvailabilitySchedule>(() =>
		convertLegacyToMultiBlock(defaultSchedule)
	);

	useEffect(() => {
		async function loadSchedule() {
			// Priority 1: Use team member's individual schedule if available
			if (selectedTeamMember) {
				const availability = selectedTeamMember.team_member_info?.availability_schedule;

				if (availability) {
					const parsedSchedule = parseScheduleData(availability);

					if (Object.keys(parsedSchedule).length > 0) {
						setSchedule(parsedSchedule);
						return;
					}
				}
			}

			// Priority 2: Use company default schedule
			try {
				const company = await fetchClinicCompany();

				if (company.schedule) {
					const parsedSchedule = parseScheduleData(company.schedule);

					if (Object.keys(parsedSchedule).length > 0) {
						setSchedule(parsedSchedule);
						return;
					}
				}
			} catch (err) {
				console.error("Error fetching company schedule:", err);
			}

			// Priority 3: Fall back to default schedule
			setSchedule(convertLegacyToMultiBlock(defaultSchedule));
		}

		loadSchedule();
	}, [selectedTeamMember]);

	return schedule;
}

export function getBlocksForDay(schedule: AvailabilitySchedule, day: string): TimeBlock[] {
	return schedule[day] || [];
}

export function isDayAvailable(schedule: AvailabilitySchedule, day: string): boolean {
	const blocks = getBlocksForDay(schedule, day);
	return blocks.length > 0 && blocks.some(block => block.start && block.end);
}

export function getDayTotalHours(schedule: AvailabilitySchedule, day: string): number {
	const blocks = getBlocksForDay(schedule, day);

	return blocks.reduce((total, block) => {
		const span = wallClockSpanMinutes(block.start, block.end);

		return span === null ? total : total + span / 60;
	}, 0);
}

// The earliest block start of the day, as "HH:mm".
export function getDayStartTime(schedule: AvailabilitySchedule, day: string): string | null {
	const starts = getBlocksForDay(schedule, day)
		.map(block => wallClockMinutes(block.start))
		.filter((minutes): minutes is number => minutes !== null);

	if (starts.length === 0) return null;

	return minutesToWallClock(Math.min(...starts));
}

// The latest block end of the day, as "HH:mm".
export function getDayEndTime(schedule: AvailabilitySchedule, day: string): string | null {
	const ends = getBlocksForDay(schedule, day)
		.map(block => wallClockMinutes(block.end))
		.filter((minutes): minutes is number => minutes !== null);

	if (ends.length === 0) return null;

	return minutesToWallClock(Math.max(...ends));
}

export function isTimeInAvailableBlock(
	schedule: AvailabilitySchedule,
	day: string,
	time: Date
): boolean {
	const blocks = getBlocksForDay(schedule, day);
	const minutes = time.getHours() * 60 + time.getMinutes();

	return blocks.some(block => isWithinWallClockBlock(block.start, block.end, minutes));
}

export function convertToLegacyFormat(schedule: AvailabilitySchedule): LegacySchedule {
	const legacySchedule: LegacySchedule = {};

	for (const [day] of Object.entries(schedule)) {
		const startTime = getDayStartTime(schedule, day);
		const endTime = getDayEndTime(schedule, day);

		legacySchedule[day] = {
			start: startTime,
			end: endTime
		};
	}

	return legacySchedule;
}